import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { t } from "../../i18n/translations";
import { Check, X, Trash2, Plus, ChevronLeft, ChevronRight, Calendar as CalIcon, List, Pencil } from "lucide-react";
import { toast } from "sonner";

function pad(n) { return n.toString().padStart(2, "0"); }
function fmtDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

const LOCATION_LABEL = { sarmenstorf: "Sarmenstorf", rupperswil: "Rupperswil" };
const locName = (k) => LOCATION_LABEL[(k || "sarmenstorf").toLowerCase()] || "Sarmenstorf";

const statusCls = (s) =>
  s === "confirmed" ? "bg-sage/15 text-sage" :
  s === "cancelled" ? "bg-destructive/10 text-destructive" :
  "bg-terracotta/10 text-terracotta";

export default function AdminBookings() {
  const { lang } = useLang();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("calendar");
  const [viewMonth, setViewMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDate, setSelectedDate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/bookings");
    setBookings(data || []);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.put(`/admin/bookings/${id}/status`, { status }); toast.success(t(lang, `admin.${status}`)); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Löschen?")) return;
    await api.delete(`/admin/bookings/${id}`);
    load();
  };

  const bookingsByDate = useMemo(() => {
    const m = {};
    bookings.forEach((b) => {
      if (b.status === "cancelled") return;
      (m[b.date] = m[b.date] || []).push(b);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.time.localeCompare(b.time)));
    return m;
  }, [bookings]);

  const filtered = bookings.filter((b) => filter === "all" || b.status === filter);

  return (
    <div className="p-5 sm:p-8 lg:p-10" data-testid="admin-bookings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl text-ink">{t(lang, "admin.bookings")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-white border border-border rounded-full p-1">
            <button onClick={() => setView("calendar")} className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 ${view === "calendar" ? "bg-ink text-white" : "text-ink-muted"}`} data-testid="view-calendar">
              <CalIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Kalender
            </button>
            <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 ${view === "list" ? "bg-ink text-white" : "text-ink-muted"}`} data-testid="view-list">
              <List className="w-3.5 h-3.5" strokeWidth={1.5} /> Liste
            </button>
          </div>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-white rounded-full text-sm hover:bg-terracotta-hover" data-testid="new-booking-btn">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> <span className="hidden sm:inline">Neue Buchung</span><span className="sm:hidden">Neu</span>
          </button>
        </div>
      </div>

      {view === "calendar" && (
        <div className="mt-6 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <CalendarMonth value={viewMonth} setValue={setViewMonth} bookingsByDate={bookingsByDate} selectedDate={selectedDate} onSelect={setSelectedDate} lang={lang} />
          </div>
          <aside>
            <div className="bg-white border border-border rounded-md p-4 sm:p-5 lg:sticky lg:top-6" data-testid="calendar-day-detail">
              <div className="text-xs uppercase tracking-widest text-terracotta mb-2">{selectedDate || "Wähle einen Tag"}</div>
              {selectedDate && (bookingsByDate[selectedDate] || []).length === 0 && (
                <div className="text-sm text-ink-muted">Keine Buchungen</div>
              )}
              <ul className="space-y-3 mt-3">
                {(bookingsByDate[selectedDate] || []).map((b) => (
                  <li key={b.id} className="border-b border-border pb-3 last:border-b-0" data-testid={`day-booking-${b.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-terracotta text-sm">{b.time}</div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusCls(b.status)}`}>{t(lang, `admin.${b.status}`)}</span>
                    </div>
                    <div className="text-sm text-ink mt-1">{b.customer_name}</div>
                    <div className="text-xs text-ink-muted">{b.service_name_snapshot}</div>
                    <div className="text-[11px] text-terracotta mt-1">📍 {locName(b.location)}</div>
                    <div className="text-xs text-ink-muted mt-1 break-all">{b.customer_email} · {b.customer_phone}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <button onClick={() => setEditingBooking(b)} className="px-2 py-1 text-[11px] rounded hover:bg-terracotta/10 text-terracotta inline-flex items-center gap-1"><Pencil className="w-3 h-3" strokeWidth={1.5}/> Ändern</button>
                      {b.status !== "confirmed" && <button onClick={() => updateStatus(b.id, "confirmed")} className="px-2 py-1 text-[11px] rounded hover:bg-sage/15 text-sage inline-flex items-center gap-1"><Check className="w-3 h-3" strokeWidth={1.5}/> Bestätigen</button>}
                      {b.status !== "cancelled" && <button onClick={() => updateStatus(b.id, "cancelled")} className="px-2 py-1 text-[11px] rounded hover:bg-destructive/10 text-destructive inline-flex items-center gap-1"><X className="w-3 h-3" strokeWidth={1.5}/> Stornieren</button>}
                      <button onClick={() => del(b.id)} className="px-2 py-1 text-[11px] rounded hover:bg-muted text-ink-muted"><Trash2 className="w-3 h-3" strokeWidth={1.5}/></button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      )}

      {view === "list" && (
        <>
          <div className="mt-5 flex gap-2 text-xs overflow-x-auto pb-1">
            {["all", "pending", "confirmed", "cancelled"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`shrink-0 px-3 py-1.5 rounded-full uppercase tracking-wider ${filter === f ? "bg-ink text-white" : "bg-white border border-border text-ink-muted"}`} data-testid={`bookings-filter-${f}`}>{t(lang, `admin.${f}`)}</button>
            ))}
          </div>

          {/* Mobile cards */}
          <div className="mt-4 space-y-3 md:hidden">
            {filtered.map((b) => (
              <div key={b.id} className="bg-white border border-border rounded-md p-4" data-testid={`booking-card-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-mono text-terracotta text-lg">{b.date}</div>
                    <div className="text-xs text-ink-muted">{b.time} · {b.service_duration} min</div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full ${statusCls(b.status)}`}>{t(lang, `admin.${b.status}`)}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-ink">{b.customer_name}</div>
                  <div className="text-xs text-ink-muted mt-0.5">{b.service_name_snapshot} · CHF {b.service_price}</div>
                  <div className="text-xs text-terracotta mt-1">📍 {locName(b.location)}</div>
                  <div className="mt-2 flex flex-col gap-1 text-sm">
                    <a href={`tel:${b.customer_phone}`} className="text-terracotta">📞 {b.customer_phone}</a>
                    <a href={`mailto:${b.customer_email}`} className="text-terracotta break-all">✉ {b.customer_email}</a>
                  </div>
                  {b.customer_birthdate && <div className="text-xs text-ink-muted mt-1">🎂 {b.customer_birthdate}</div>}
                  {b.notes && <div className="text-xs text-ink-muted mt-2 italic">„{b.notes}"</div>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => setEditingBooking(b)} className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-terracotta/10 text-terracotta rounded-md" data-testid={`edit-mobile-${b.id}`}><Pencil className="w-3.5 h-3.5" strokeWidth={1.5}/> Ändern</button>
                  {b.status !== "confirmed" && <button onClick={() => updateStatus(b.id, "confirmed")} className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-sage/15 text-sage rounded-md"><Check className="w-3.5 h-3.5" strokeWidth={1.5}/> Bestätigen</button>}
                  {b.status !== "cancelled" && <button onClick={() => updateStatus(b.id, "cancelled")} className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-destructive/10 text-destructive rounded-md"><X className="w-3.5 h-3.5" strokeWidth={1.5}/> Stornieren</button>}
                  <button onClick={() => del(b.id)} className="inline-flex items-center justify-center px-3 py-2 text-xs bg-muted text-ink-muted rounded-md"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div className="text-center py-10 text-ink-muted">—</div>}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block mt-4 bg-white border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-5 py-3">{t(lang, "admin.date")}</th>
                  <th className="text-left px-5 py-3">{t(lang, "admin.customer")}</th>
                  <th className="text-left px-5 py-3">{t(lang, "admin.service")}</th>
                  <th className="text-left px-5 py-3">{t(lang, "admin.contact")}</th>
                  <th className="text-left px-5 py-3">{t(lang, "admin.status")}</th>
                  <th className="text-right px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`booking-row-${b.id}`}>
                    <td className="px-5 py-4 align-top"><div className="text-ink font-medium">{b.date}</div><div className="text-xs text-ink-muted">{b.time}</div></td>
                    <td className="px-5 py-4 align-top">
                      <div className="text-ink">{b.customer_name}</div>
                      {b.customer_birthdate && <div className="text-xs text-ink-muted">🎂 {b.customer_birthdate}</div>}
                      {(b.customer_address || b.customer_city) && <div className="text-xs text-ink-muted">📍 {[b.customer_address, [b.customer_postal_code, b.customer_city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>}
                      {b.notes && <div className="text-xs text-ink-muted mt-1 max-w-xs italic">{b.notes}</div>}
                    </td>
                    <td className="px-5 py-4 align-top"><div className="text-ink">{b.service_name_snapshot}</div><div className="text-xs text-ink-muted">CHF {b.service_price} · {b.service_duration} min</div><div className="text-xs text-terracotta mt-1">📍 {locName(b.location)}</div></td>
                    <td className="px-5 py-4 align-top text-xs"><a href={`mailto:${b.customer_email}`} className="text-ink hover:text-terracotta block">{b.customer_email}</a><a href={`tel:${b.customer_phone}`} className="text-ink-muted hover:text-terracotta">{b.customer_phone}</a></td>
                    <td className="px-5 py-4 align-top"><span className={`text-xs px-2.5 py-1 rounded-full ${statusCls(b.status)}`}>{t(lang, `admin.${b.status}`)}</span></td>
                    <td className="px-5 py-4 align-top text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setEditingBooking(b)} className="p-2 rounded hover:bg-terracotta/10 text-terracotta" data-testid={`edit-${b.id}`} title="Ändern"><Pencil className="w-4 h-4" strokeWidth={1.5}/></button>
                        {b.status !== "confirmed" && <button onClick={() => updateStatus(b.id, "confirmed")} className="p-2 rounded hover:bg-sage/15 text-sage" data-testid={`confirm-${b.id}`} title="Bestätigen"><Check className="w-4 h-4" strokeWidth={1.5}/></button>}
                        {b.status !== "cancelled" && <button onClick={() => updateStatus(b.id, "cancelled")} className="p-2 rounded hover:bg-destructive/10 text-destructive" data-testid={`cancel-${b.id}`} title="Stornieren"><X className="w-4 h-4" strokeWidth={1.5}/></button>}
                        <button onClick={() => del(b.id)} className="p-2 rounded hover:bg-muted text-ink-muted" data-testid={`delete-${b.id}`} title="Löschen"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-ink-muted text-sm">—</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showForm && <BookingForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} prefillDate={selectedDate} />}
      {editingBooking && <EditBookingForm booking={editingBooking} onClose={() => setEditingBooking(null)} onSaved={() => { setEditingBooking(null); load(); }} />}
    </div>
  );
}

function CalendarMonth({ value, setValue, bookingsByDate, selectedDate, onSelect, lang }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const firstDay = new Date(value.getFullYear(), value.getMonth(), 1);
  const lastDay = new Date(value.getFullYear(), value.getMonth() + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startWeekday; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(value.getFullYear(), value.getMonth(), d));
  const monthName = value.toLocaleDateString(lang, { month: "long", year: "numeric" });
  const dayShort = ["mon","tue","wed","thu","fri","sat","sun"];

  return (
    <div className="bg-white border border-border rounded-md p-3 sm:p-5" data-testid="admin-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setValue(new Date(value.getFullYear(), value.getMonth() - 1, 1))} className="p-2 hover:bg-muted rounded" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/></button>
        <span className="font-display text-xl sm:text-2xl capitalize">{monthName}</span>
        <button onClick={() => setValue(new Date(value.getFullYear(), value.getMonth() + 1, 1))} className="p-2 hover:bg-muted rounded" data-testid="cal-next"><ChevronRight className="w-4 h-4" strokeWidth={1.5}/></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-[9px] sm:text-[11px] uppercase tracking-wider text-ink-muted text-center mb-1">
        {dayShort.map((d) => <div key={d}>{t(lang, `days_short.${d}`)}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = fmtDate(d);
          const isToday = ds === fmtDate(today);
          const isSel = selectedDate === ds;
          const dayBookings = bookingsByDate[ds] || [];
          return (
            <button
              key={i}
              onClick={() => onSelect(ds)}
              data-testid={`admin-cal-day-${ds}`}
              className={`aspect-square p-1 sm:p-1.5 rounded-md text-xs sm:text-sm relative transition-colors flex flex-col items-start ${isSel ? "bg-terracotta text-white" : isToday ? "bg-sand-soft border border-terracotta/30" : "hover:bg-muted"}`}
            >
              <span className={isSel ? "text-white" : "text-ink"}>{d.getDate()}</span>
              {dayBookings.length > 0 && (
                <div className="mt-auto w-full">
                  <div className={`hidden sm:block text-[9px] uppercase tracking-wider ${isSel ? "text-white/80" : "text-terracotta"}`}>{dayBookings.length}×</div>
                  <div className="flex gap-0.5 mt-0.5 flex-wrap">
                    {dayBookings.slice(0, 3).map((b, idx) => (
                      <span key={idx} className={`block w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${isSel ? "bg-white/80" : b.status === "confirmed" ? "bg-sage" : "bg-terracotta"}`} />
                    ))}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BookingForm({ onClose, onSaved, prefillDate }) {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    service_id: "", customer_name: "", customer_birthdate: "", customer_email: "", customer_phone: "",
    date: prefillDate || fmtDate(new Date()), time: "10:00", location: "sarmenstorf", notes: "", status: "confirmed", send_email: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/admin/services").then((r) => setServices((r.data || []).filter((s) => s.active))); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_id || !form.customer_name || !form.customer_email || !form.date || !form.time) return;
    setSubmitting(true);
    try {
      await api.post("/admin/bookings", { ...form, language: "de" });
      toast.success("Buchung gespeichert");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-auto" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="booking-form-modal">
      <form onSubmit={submit} className="bg-white rounded-md max-w-2xl w-full p-5 sm:p-8 my-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-ink">Neue Buchung</h2>
            <p className="text-sm text-ink-muted mt-1">Manuell eingetragen.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-muted rounded" data-testid="booking-form-close"><X className="w-5 h-5" strokeWidth={1.5}/></button>
        </div>
        <div className="mt-5 space-y-4">
          <Field label="Behandlung *">
            <select required value={form.service_id} onChange={(e) => set("service_id", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md bg-white" data-testid="form-service-select">
              <option value="">— Auswählen —</option>
              {services.map((s) => (<option key={s.id} value={s.id}>{s.name?.de || s.slug} · {s.duration_minutes} min · CHF {s.price_chf}</option>))}
            </select>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name *"><input required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="form-name" /></Field>
            <Field label="Geburtstag"><input type="date" value={form.customer_birthdate} onChange={(e) => set("customer_birthdate", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="form-birthdate" /></Field>
            <Field label="E-Mail *"><input required type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="form-email" /></Field>
            <Field label="Telefon *"><input required value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="form-phone" /></Field>
            <Field label="Datum *"><input required type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="form-date" /></Field>
            <Field label="Uhrzeit *"><input required type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="form-time" /></Field>
          </div>
          <Field label="Standort *">
            <select required value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md bg-white" data-testid="form-location">
              <option value="sarmenstorf">Sarmenstorf · Obere Feldeggstr. 13</option>
              <option value="rupperswil">Rupperswil · Aarestrasse 20</option>
            </select>
          </Field>
          <Field label="Anmerkungen"><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="form-notes" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md bg-white" data-testid="form-status">
                <option value="confirmed">Bestätigt</option>
                <option value="pending">Offen</option>
              </select>
            </Field>
            <label className="flex items-end gap-2 text-sm pb-2"><input type="checkbox" checked={form.send_email} onChange={(e) => set("send_email", e.target.checked)} data-testid="form-send-email" /><span>E-Mail-Bestätigung senden</span></label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border rounded-md">Abbrechen</button>
          <button type="submit" disabled={submitting} className="px-6 py-2.5 bg-terracotta text-white rounded-md disabled:opacity-30" data-testid="form-submit">{submitting ? "…" : "Speichern"}</button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-ink-muted">{label}</span>
    {children}
  </label>
);

function EditBookingForm({ booking, onClose, onSaved }) {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    service_id: booking.service_id || "",
    customer_name: booking.customer_name || "",
    customer_email: booking.customer_email || "",
    customer_phone: booking.customer_phone || "",
    customer_birthdate: booking.customer_birthdate || "",
    customer_address: booking.customer_address || "",
    customer_postal_code: booking.customer_postal_code || "",
    customer_city: booking.customer_city || "",
    date: booking.date || "",
    time: booking.time || "",
    location: booking.location || "sarmenstorf",
    notes: booking.notes || "",
    notify_customer: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/admin/services").then((r) => setServices((r.data || []).filter((s) => s.active))); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/admin/bookings/${booking.id}`, form);
      toast.success("Buchung aktualisiert");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-auto" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="edit-booking-modal">
      <form onSubmit={submit} className="bg-white rounded-md max-w-2xl w-full p-5 sm:p-8 my-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-ink">Buchung ändern</h2>
            <p className="text-sm text-ink-muted mt-1">Alle Felder können angepasst werden.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-muted rounded" data-testid="edit-booking-close"><X className="w-5 h-5" strokeWidth={1.5}/></button>
        </div>
        <div className="mt-5 space-y-4">
          <Field label="Behandlung">
            <select value={form.service_id} onChange={(e) => set("service_id", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md bg-white" data-testid="edit-service-select">
              {services.map((s) => (<option key={s.id} value={s.id}>{s.name?.de || s.slug} · {s.duration_minutes} min · CHF {s.price_chf}</option>))}
            </select>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-name" /></Field>
            <Field label="Geburtstag"><input type="date" value={form.customer_birthdate} onChange={(e) => set("customer_birthdate", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-birthdate" /></Field>
            <Field label="E-Mail"><input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-email" /></Field>
            <Field label="Telefon"><input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-phone" /></Field>
            <Field label="Strasse + Nr."><input value={form.customer_address} onChange={(e) => set("customer_address", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-address" /></Field>
            <Field label="PLZ + Ort">
              <div className="mt-2 flex gap-2">
                <input value={form.customer_postal_code} onChange={(e) => set("customer_postal_code", e.target.value)} placeholder="PLZ" className="w-24 px-3 py-2.5 border border-border rounded-md" data-testid="edit-zip" />
                <input value={form.customer_city} onChange={(e) => set("customer_city", e.target.value)} placeholder="Ort" className="flex-1 px-3 py-2.5 border border-border rounded-md" data-testid="edit-city" />
              </div>
            </Field>
            <Field label="Datum"><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-date" /></Field>
            <Field label="Uhrzeit"><input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-time" /></Field>
          </div>
          <Field label="Standort">
            <select value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md bg-white" data-testid="edit-location">
              <option value="sarmenstorf">Sarmenstorf · Obere Feldeggstr. 13</option>
              <option value="rupperswil">Rupperswil · Aarestrasse 20</option>
            </select>
          </Field>
          <Field label="Anmerkungen"><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="edit-notes" /></Field>
          <label className="flex items-center gap-2 text-sm bg-sand-soft p-3 rounded-md">
            <input type="checkbox" checked={form.notify_customer} onChange={(e) => set("notify_customer", e.target.checked)} data-testid="edit-notify" />
            <span>Kundin per E-Mail über die Änderung informieren</span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border rounded-md">Abbrechen</button>
          <button type="submit" disabled={submitting} className="px-6 py-2.5 bg-terracotta text-white rounded-md disabled:opacity-30" data-testid="edit-submit">{submitting ? "…" : "Änderungen speichern"}</button>
        </div>
      </form>
    </div>
  );
}
